import React, { useEffect, useState } from 'react';
import { User, Application, ApplicationStatus, JobPosting, Interview } from '../types';
import { api } from '../services/api';
import { Shield, Users, CheckCircle2, Trash2, Calendar, Video, Edit3, UserCheck, ShieldAlert, PlusCircle } from 'lucide-react';

interface HiringDashboardProps {
  user: User;
  onOpenCreateJob: () => void;
  onLaunchVideoRoom: (interview: Interview) => void;
  onScheduleInterview: (app: Application) => void;
  onJobsUpdated: () => void;
}

export const HiringDashboard: React.FC<HiringDashboardProps> = ({
  user,
  onOpenCreateJob,
  onLaunchVideoRoom,
  onScheduleInterview,
  onJobsUpdated,
}) => {
  const [activeSubTab, setActiveSubTab] = useState<'applications' | 'interviews' | 'hr-approvals' | 'manage-jobs'>('applications');
  
  const [applications, setApplications] = useState<Application[]>([]);
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [jobsList, setJobsList] = useState<JobPosting[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  const [statusInput, setStatusInput] = useState<ApplicationStatus>('submitted');
  const [notesInput, setNotesInput] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('All');
  const [updating, setUpdating] = useState(false);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [appsRes, intsRes, jobsRes] = await Promise.all([
        api.getAllApplications(),
        api.getAllInterviews(),
        api.getJobs(),
      ]);
      setApplications(appsRes.applications || []);
      setInterviews(intsRes.interviews || []);
      setJobsList(jobsRes.jobs || []);

      if (user.role === 'admin' || user.role === 'hiring_team') {
        const usersRes = await api.getUsers().catch(() => ({ users: [] }));
        setAllUsers(usersRes.users || []);
      }
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [user]);

  const handleOpenReview = (app: Application) => {
    setSelectedApp(app);
    setStatusInput(app.status);
    setNotesInput(app.reviewer_notes || '');
  };

  const handleSaveStatus = async () => {
    if (!selectedApp) return;
    setUpdating(true);

    try {
      await api.updateApplicationStatus(selectedApp.id, statusInput, notesInput);
      setSelectedApp(null);
      fetchDashboardData();
    } catch (err: any) {
      alert(err.message || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteJob = async (jobId: string, jobTitle: string) => {
    if (window.confirm(`Are you sure you want to permanently delete position "${jobTitle}"?`)) {
      try {
        await api.deleteJob(jobId);
        fetchDashboardData();
        onJobsUpdated();
      } catch (err: any) {
        alert(err.message || 'Failed to delete job posting');
      }
    }
  };

  const handleApproveHrUser = async (userId: string, targetRole: string) => {
    try {
      await api.approveUser(userId, targetRole, true);
      alert('User HR access approved successfully!');
      fetchDashboardData();
    } catch (err: any) {
      alert(err.message || 'Failed to approve user');
    }
  };

  // Metrics
  const totalApps = applications.length;
  const inReview = applications.filter(a => a.status === 'submitted' || a.status === 'under_review').length;
  const interviewingCount = applications.filter(a => a.status === 'interviewing').length;
  const pendingHrCount = allUsers.filter(u => u.role === 'hiring_team' && !u.hr_approved).length;

  const filteredApps = filterStatus === 'All' 
    ? applications 
    : applications.filter(a => a.status === filterStatus);

  return (
    <div style={{ padding: '40px 0' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--color-aqua)', fontWeight: 700, fontSize: '0.85rem' }}>
            <Shield size={16} /> HR & Hiring Portal
          </div>
          <h2 style={{ fontSize: '1.8rem', fontWeight: 800, marginTop: '4px' }}>
            Candidate Pipeline & HR Administration
          </h2>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn-secondary" onClick={fetchDashboardData}>
            Refresh Data
          </button>
          <button className="btn-primary" onClick={onOpenCreateJob}>
            <PlusCircle size={16} /> Post New Job
          </button>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="stats-grid" style={{ maxWidth: '100%', marginBottom: '32px' }}>
        <div className="stat-card">
          <div className="stat-number">{totalApps}</div>
          <div className="stat-label">Total Applications</div>
        </div>
        <div className="stat-card">
          <div className="stat-number" style={{ color: 'var(--accent-amber)' }}>{inReview}</div>
          <div className="stat-label">Pending Review</div>
        </div>
        <div className="stat-card">
          <div className="stat-number" style={{ color: 'var(--color-aqua)' }}>{interviews.length}</div>
          <div className="stat-label">Scheduled Interviews</div>
        </div>
        <div className="stat-card">
          <div className="stat-number" style={{ color: pendingHrCount > 0 ? 'var(--accent-rose)' : 'var(--accent-emerald)' }}>
            {pendingHrCount}
          </div>
          <div className="stat-label">Pending HR Approvals</div>
        </div>
      </div>

      {/* Main Navigation Tabs */}
      <div className="tabs-nav">
        <button
          className={`tab-button ${activeSubTab === 'applications' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('applications')}
        >
          <Users size={16} /> Candidate Pipeline ({applications.length})
        </button>
        <button
          className={`tab-button ${activeSubTab === 'interviews' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('interviews')}
        >
          <Video size={16} /> Scheduled Interviews ({interviews.length})
        </button>
        <button
          className={`tab-button ${activeSubTab === 'manage-jobs' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('manage-jobs')}
        >
          <Shield size={16} /> Job Postings & Deletion ({jobsList.length})
        </button>
        <button
          className={`tab-button ${activeSubTab === 'hr-approvals' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('hr-approvals')}
        >
          <UserCheck size={16} /> HR Access Approvals {pendingHrCount > 0 && <span style={{ background: 'var(--accent-rose)', color: '#fff', fontSize: '0.7rem', padding: '2px 6px', borderRadius: '10px', marginLeft: '4px' }}>{pendingHrCount}</span>}
        </button>
      </div>

      {/* SUBTAB 1: Candidate Applications */}
      {activeSubTab === 'applications' && (
        <>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
            {['All', 'submitted', 'under_review', 'interviewing', 'offered', 'rejected'].map(status => (
              <button
                key={status}
                className={`btn-secondary ${filterStatus === status ? 'active' : ''}`}
                style={{ fontSize: '0.8rem', padding: '6px 12px' }}
                onClick={() => setFilterStatus(status)}
              >
                {status === 'All' ? 'All Stages' : status.replace('_', ' ').toUpperCase()}
              </button>
            ))}
          </div>

          {filteredApps.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', background: 'var(--color-navy-card)', borderRadius: 'var(--radius-lg)' }}>
              <Users size={48} color="var(--text-dim)" style={{ marginBottom: '12px' }} />
              <h3 style={{ fontSize: '1.2rem', fontWeight: 700 }}>No candidates found in this stage</h3>
            </div>
          ) : (
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Applicant</th>
                    <th>Applied Position</th>
                    <th>Discord / IGN</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApps.map((app) => (
                    <tr key={app.id}>
                      <td>
                        <div>
                          <strong style={{ color: 'var(--text-main)' }}>{app.applicant_name}</strong>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>{app.applicant_email}</div>
                        </div>
                      </td>
                      <td>
                        <span style={{ fontWeight: 600 }}>{app.job_title}</span>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-aqua)' }}>{app.job_department}</div>
                      </td>
                      <td>
                        <div style={{ fontSize: '0.85rem' }}>
                          {app.discord_tag ? <span>Discord: <strong>{app.discord_tag}</strong></span> : 'N/A'}
                        </div>
                      </td>
                      <td style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                        {new Date(app.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <span className={`status-pill ${app.status}`}>
                          {app.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn-secondary"
                          style={{ padding: '6px 12px', fontSize: '0.8rem' }}
                          onClick={() => handleOpenReview(app)}
                        >
                          <Edit3 size={14} /> Review Candidate
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* SUBTAB 2: Scheduled Video Interviews */}
      {activeSubTab === 'interviews' && (
        <div className="data-table-wrapper">
          {interviews.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <Video size={48} color="var(--color-aqua)" style={{ marginBottom: '12px' }} />
              <h3 style={{ fontSize: '1.2rem', fontWeight: 700 }}>No Video Interviews Scheduled Yet</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '4px' }}>
                Open any candidate in the Candidate Pipeline tab and click "Schedule Video Interview".
              </p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Position</th>
                  <th>Scheduled Date & Time</th>
                  <th>Room ID</th>
                  <th>Interviewer</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {interviews.map((int) => (
                  <tr key={int.id}>
                    <td>
                      <strong>{int.candidate_name}</strong>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>{int.candidate_email}</div>
                    </td>
                    <td style={{ fontWeight: 600 }}>{int.job_title}</td>
                    <td style={{ fontSize: '0.85rem', color: 'var(--color-aqua)', fontWeight: 600 }}>
                      <Calendar size={14} style={{ display: 'inline', marginRight: '4px' }} />
                      {new Date(int.scheduled_at).toLocaleString()}
                    </td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem' }}>{int.room_id}</td>
                    <td style={{ fontSize: '0.85rem' }}>{int.interviewer_email}</td>
                    <td>
                      <button
                        className="btn-primary"
                        style={{ padding: '6px 14px', fontSize: '0.8rem' }}
                        onClick={() => onLaunchVideoRoom(int)}
                      >
                        <Video size={14} /> Join Call Room
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* SUBTAB 3: Job Postings & Deletion */}
      {activeSubTab === 'manage-jobs' && (
        <div className="data-table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Department</th>
                <th>Type</th>
                <th>Location</th>
                <th>Compensation</th>
                <th>Posted Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobsList.map((job) => (
                <tr key={job.id}>
                  <td style={{ fontWeight: 700 }}>{job.title}</td>
                  <td><span className={`dept-badge ${job.department.replace(/\s+/g, '')}`}>{job.department}</span></td>
                  <td style={{ fontSize: '0.85rem' }}>{job.type}</td>
                  <td style={{ fontSize: '0.85rem' }}>{job.location}</td>
                  <td style={{ fontSize: '0.85rem', color: 'var(--accent-emerald)', fontWeight: 600 }}>{job.salary_range}</td>
                  <td style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>{new Date(job.created_at).toLocaleDateString()}</td>
                  <td>
                    <button
                      className="btn-secondary"
                      style={{ padding: '6px 12px', fontSize: '0.8rem', color: 'var(--accent-rose)', borderColor: 'rgba(244,63,94,0.3)' }}
                      onClick={() => handleDeleteJob(job.id, job.title)}
                    >
                      <Trash2 size={14} /> Delete Job
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* SUBTAB 4: HR Access Approvals */}
      {activeSubTab === 'hr-approvals' && (
        <div className="data-table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Requested Role</th>
                <th>HR Approval Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {allUsers.map((u) => (
                <tr key={u.id}>
                  <td><strong>{u.name}</strong></td>
                  <td>{u.email}</td>
                  <td><span className={`role-badge ${u.role}`}>{u.role}</span></td>
                  <td>
                    {u.hr_approved ? (
                      <span className="status-pill offered"><CheckCircle2 size={12} /> Approved</span>
                    ) : (
                      <span className="status-pill under_review"><ShieldAlert size={12} /> Pending Admin Approval</span>
                    )}
                  </td>
                  <td>
                    {!u.hr_approved && (
                      <button
                        className="btn-primary"
                        style={{ padding: '6px 12px', fontSize: '0.8rem' }}
                        onClick={() => handleApproveHrUser(u.id, u.role)}
                      >
                        Approve HR Access
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Candidate Reviewer Modal */}
      {selectedApp && (
        <div className="modal-overlay" onClick={() => setSelectedApp(null)}>
          <div className="modal-content" style={{ maxWidth: '680px' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ marginBottom: '20px' }}>
              <span className={`status-pill ${selectedApp.status}`}>
                {selectedApp.status.replace('_', ' ')}
              </span>
              <h2 style={{ fontSize: '1.6rem', fontWeight: 800, marginTop: '8px' }}>
                Reviewing Candidate: {selectedApp.applicant_name}
              </h2>
              <p style={{ color: 'var(--color-aqua)', fontSize: '0.9rem', fontWeight: 600 }}>
                {selectedApp.job_title} ({selectedApp.job_department})
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
              <button
                className="btn-primary"
                style={{ fontSize: '0.85rem' }}
                onClick={() => {
                  const targetApp = selectedApp;
                  setSelectedApp(null);
                  onScheduleInterview(targetApp);
                }}
              >
                <Video size={16} /> Schedule Video Interview
              </button>
            </div>

            {/* HR Action Form */}
            <div style={{ borderTop: '1px solid var(--color-beige-border)', paddingTop: '20px' }}>
              <h4 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '12px', color: 'var(--color-aqua)' }}>
                Update Application Pipeline Status
              </h4>

              <div className="form-group">
                <label className="form-label">Stage Status</label>
                <select
                  className="form-select"
                  value={statusInput}
                  onChange={(e) => setStatusInput(e.target.value as ApplicationStatus)}
                >
                  <option value="submitted">Submitted</option>
                  <option value="under_review">Under Review</option>
                  <option value="interviewing">Interviewing</option>
                  <option value="offered">Offer Extended</option>
                  <option value="rejected">Declined / Rejected</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Reviewer Notes & Feedback</label>
                <textarea
                  className="form-textarea"
                  value={notesInput}
                  onChange={(e) => setNotesInput(e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
                <button className="btn-secondary" onClick={() => setSelectedApp(null)}>
                  Close
                </button>
                <button className="btn-primary" onClick={handleSaveStatus} disabled={updating}>
                  {updating ? 'Saving...' : 'Save Pipeline Status'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
